import React, { useState, useEffect, useMemo } from 'react';
import { FileUpload } from '../../../../../components/ui/FileUpload';
import { Label } from '../../../../../components/ui/label';
// Alert component not available, using div instead
import { Badge } from '../../../../../components/ui/badge';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { getListMaterialCategory, AddMaterialCategory } from '../../../../../redux/Project/materialSaga';
import { SPEC_TAG_TYPES } from '../../../constants/specificationConstants';
import customNotification from '../../../../../components/ui/custom-notification';
import AddNewItemDialog from '../../../../../components/ui/AddNewItemDialog';
import { MAX_UPLOAD_FILE_SIZE_BYTES, MAX_UPLOAD_FILE_SIZE_LABEL } from '../../../../../constants/fileUpload';

// File validation types and interfaces
interface FileValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  fileType: string;
  category: 'document' | 'image' | 'ndt' | 'unknown';
  ndtTechnique?: string;
}

interface NDTValidationResult extends FileValidationResult {
  ndtTechnique?: string;
  ndtSpecificErrors?: string[];
  ndtSpecificWarnings?: string[];
}

// NDT file type definitions
const NDT_FILE_TYPES = {
  RT: {
    extensions: ['.dcm', '.tiff', '.tif', '.png', '.jpg', '.jpeg'],
    description: 'Radiographic Testing (RT)',
    maxSize: MAX_UPLOAD_FILE_SIZE_BYTES,
    requiredMetadata: ['exposure', 'kvp', 'ma', 'time']
  },
  UT: {
    extensions: ['.utf', '.rdt', '.dta', '.csv', '.xlsx'],
    description: 'Ultrasonic Testing (UT)',
    maxSize: MAX_UPLOAD_FILE_SIZE_BYTES,
    requiredMetadata: ['frequency', 'gain', 'range']
  },
  MT: {
    extensions: ['.jpg', '.jpeg', '.png', '.tiff', '.tif'],
    description: 'Magnetic Particle Testing (MT)',
    maxSize: MAX_UPLOAD_FILE_SIZE_BYTES,
    requiredMetadata: ['magnetic_field', 'particle_type']
  },
  PT: {
    extensions: ['.jpg', '.jpeg', '.png', '.tiff', '.tif'],
    description: 'Dye Penetrant Testing (PT)',
    maxSize: MAX_UPLOAD_FILE_SIZE_BYTES,
    requiredMetadata: ['penetrant_type', 'developer_type']
  },
  ET: {
    extensions: ['.edd', '.ect', '.csv', '.xlsx'],
    description: 'Eddy Current Testing (ET)',
    maxSize: MAX_UPLOAD_FILE_SIZE_BYTES,
    requiredMetadata: ['frequency', 'coil_type', 'lift_off']
  }
};

// Base file type definitions
const BASE_FILE_TYPES = {
  PDF: {
    extensions: ['.pdf'],
    description: 'PDF Documents',
    maxSize: MAX_UPLOAD_FILE_SIZE_BYTES,
    category: 'document'
  },
  EXCEL: {
    extensions: ['.xlsx', '.xls', '.csv'],
    description: 'Excel/CSV Files',
    maxSize: MAX_UPLOAD_FILE_SIZE_BYTES,
    category: 'document'
  },
  WORD: {
    extensions: ['.doc', '.docx'],
    description: 'Word Documents',
    maxSize: MAX_UPLOAD_FILE_SIZE_BYTES,
    category: 'document'
  },
  IMAGE: {
    extensions: ['.jpg', '.jpeg', '.png', '.tiff', '.tif', '.bmp'],
    description: 'Image Files',
    maxSize: MAX_UPLOAD_FILE_SIZE_BYTES,
    category: 'image'
  }
};

interface DocumentUploadProps {
  onUpload: (files: FileList | null) => void;
  acceptedTypes?: string;
  multiple?: boolean;
  maxFiles?: number;
  buttonText?: string;
  className?: string;
  variant?: 'default' | 'outline' | 'secondary' | 'destructive' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  label?: string;
  description?: string;
  required?: boolean;
  enableNDTValidation?: boolean;
  ndtTechnique?: 'RT' | 'UT' | 'MT' | 'PT' | 'ET' | 'ALL';
  // Tagging props
  enableTagging?: boolean;
  specificationType?: string;
  projectId?: number;
  onTagsChange?: (fileIndex: number, tags: string[]) => void;
  selectedTags?: { [fileIndex: number]: string[] };
}

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  onUpload,
  acceptedTypes = '.pdf,.doc,.docx,.xlsx,.xls,.jpg,.jpeg,.png,.tiff,.tif,.bmp,.dcm,.utf,.rdt,.dta,.edd,.ect',
  multiple = false,
  maxFiles = 1,
  buttonText = multiple ? 'Upload Documents' : 'Upload Document',
  className = '',
  variant = 'outline',
  size = 'lg',
  label = 'File',
  description = 'Upload the specification documents (PDF, Excel, DOC, Images, NDT files, etc...)',
  required = true,
  enableNDTValidation = false,
  ndtTechnique = 'ALL',
  // Tagging props
  enableTagging = false,
  specificationType = '',
  projectId,
  onTagsChange,
  selectedTags = {}
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [validationResults, setValidationResults] = useState<FileValidationResult[]>([]);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  
  // Tagging state
  const [localDocumentTags, setLocalDocumentTags] = useState<any[]>([]);
  const [isAddingTag, setIsAddingTag] = useState(false);

  // Tag management functions
  const getSpecificationTags = (specType: string) => {
    const tagType = SPEC_TAG_TYPES[specType];
    if (!tagType) return [];
    
    return localDocumentTags.filter((tag: any) => tag.tags_type === tagType);
  };

  // Convert specification tags to options for the MultiSelect component
  const tagOptions = useMemo(() => {
    if (!specificationType || !enableTagging) return [];
    
    return getSpecificationTags(specificationType).map(tag => ({
      value: tag.tags_id.toString(),
      label: tag.tags_name
    }));
  }, [localDocumentTags, specificationType, enableTagging]);

  // Load tags on component mount
  useEffect(() => {
    const loadTags = async () => {
      if (enableTagging) {
        try {
          const allTags = await getListMaterialCategory();
          if (allTags && allTags.length > 0) {
            setLocalDocumentTags(allTags);
          }
        } catch (error) {
          console.error('Failed to load tags:', error);
        }
      }
    };
    
    loadTags();
  }, [enableTagging]);

  const handleAddTag = async (newTagName: string) => {
    if (newTagName.trim()) {
      if (!specificationType) {
        customNotification.warning('Please provide a specification type for tags');
        return;
      }

      const tagType = SPEC_TAG_TYPES[specificationType];
      if (!tagType) {
        customNotification.warning('Invalid specification type for tags');
        return;
      }

      // Check if tag already exists
      if (getSpecificationTags(specificationType).find((tag: any) => tag.tags_name === newTagName.trim())) {
        customNotification.warning(`${newTagName.trim()} tag already exists`);
        return;
      }

      const apiPayload = {
        tags_name: newTagName.trim(),
        project_id: projectId,
        tags_type: tagType
      };

      const res = await AddMaterialCategory(apiPayload, 'specification tag');
      if (res) {
        // Update local tags
        setLocalDocumentTags(prev => [...prev, res]);
        
        // Automatically select the newly added tag for the first file if it exists
        if (files.length > 0 && onTagsChange) {
          const newTagValue = res.tags_id.toString();
          onTagsChange(0, [...(selectedTags[0] || []), newTagValue]);
        }
        
      //  customNotification.success('Tag Added', `${newTagName.trim()} has been added successfully.`);
      }
    }
  };

  const handleTagsChange = (fileIndex: number, tags: string[]) => {
    if (onTagsChange) {
      onTagsChange(fileIndex, tags);
    }
  };

  // File validation function
  const validateFile = (file: File): FileValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
    
    // Check file size
    if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
      errors.push(`File size exceeds maximum limit (${MAX_UPLOAD_FILE_SIZE_LABEL})`);
    }

    // Determine file category and validate
    let category: 'document' | 'image' | 'ndt' | 'unknown' = 'unknown';
    let fileType = 'Unknown';
    let ndtTechniqueType: string | undefined;

    // Check if it's an NDT file
    for (const [technique, config] of Object.entries(NDT_FILE_TYPES)) {
      if (config.extensions.includes(fileExtension)) {
        category = 'ndt';
        fileType = config.description;
        ndtTechniqueType = technique;
        
        // NDT-specific validation
        if (file.size > config.maxSize) {
          errors.push(`${technique} file size exceeds limit (${MAX_UPLOAD_FILE_SIZE_LABEL})`);
        }
        
        // Check for required metadata (this would be enhanced in a real implementation)
        if (config.requiredMetadata.length > 0) {
          warnings.push(`Ensure ${technique} file contains required metadata: ${config.requiredMetadata.join(', ')}`);
        }
        break;
      }
    }

    // Check if it's a base file type
    if (category === 'unknown') {
      for (const [type, config] of Object.entries(BASE_FILE_TYPES)) {
        if (config.extensions.includes(fileExtension)) {
          category = config.category as 'document' | 'image' | 'ndt' | 'unknown';
          fileType = config.description;
          
          if (file.size > config.maxSize) {
            errors.push(`${type} file size exceeds limit (${MAX_UPLOAD_FILE_SIZE_LABEL})`);
          }
          break;
        }
      }
    }

    // If still unknown, add warning
    if (category === 'unknown') {
      warnings.push('File type not recognized. Please ensure it\'s a supported format.');
      fileType = 'Unknown Format';
    }

    // NDT technique specific validation
    if (enableNDTValidation && ndtTechnique !== 'ALL' && ndtTechniqueType && ndtTechniqueType !== ndtTechnique) {
      errors.push(`File type (${ndtTechniqueType}) does not match selected NDT technique (${ndtTechnique})`);
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      fileType,
      category,
      ...(category === 'ndt' && {
        ndtTechnique: ndtTechniqueType,
        ndtSpecificErrors: errors.filter(e => e.includes(ndtTechniqueType!)),
        ndtSpecificWarnings: warnings.filter(w => w.includes(ndtTechniqueType!))
      })
    };
  };

  const handleFileSelect = (incomingFiles: FileList | null) => {
    if (!incomingFiles) {
      return;
    }

    const fileArray = Array.from(incomingFiles);
    const results = fileArray.map(validateFile);
    const hasErrors = results.some((result) => !result.isValid);

    if (hasErrors) {
      const firstError = results.find((r) => !r.isValid)?.errors[0];
      if (firstError) {
        customNotification.error('File validation failed', firstError);
      }
      return;
    }

    setValidationResults((prev) => (multiple ? [...prev, ...results].slice(0, maxFiles) : results.slice(0, 1)));
    setHasValidationErrors(false);

    setFiles((prevFiles) => {
      const merged = multiple
        ? [...prevFiles, ...fileArray].slice(0, maxFiles)
        : fileArray.slice(0, 1);
      const dt = new DataTransfer();
      merged.forEach((file) => dt.items.add(file));
      onUpload(dt.files);
      return merged;
    });
  };

  const handleRemoveFile = (index: number) => {
    setFiles(prevFiles => {
      const nextFiles = prevFiles.filter((_, i) => i !== index);
      if (nextFiles.length === 0) {
        onUpload(null);
      } else {
        const dt = new DataTransfer();
        nextFiles.forEach((file) => dt.items.add(file));
        onUpload(dt.files);
      }
      return nextFiles;
    });
    setValidationResults(prevResults => prevResults.filter((_, i) => i !== index));
    
    // Recheck validation errors
    const remainingResults = validationResults.filter((_, i) => i !== index);
    const hasErrors = remainingResults.some(result => !result.isValid);
    setHasValidationErrors(hasErrors);
  };

  const getValidationIcon = (result: FileValidationResult) => {
    if (result.errors.length > 0) {
      return <XCircle className="h-4 w-4 text-hero-red" />;
    } else if (result.warnings.length > 0) {
      return <AlertTriangle className="h-4 w-4 text-[#B38600]" />;
    } else {
      return <CheckCircle className="h-4 w-4 text-galvanizing-green" />;
    }
  };

  const getValidationColor = (result: FileValidationResult) => {
    if (result.errors.length > 0) {
      return ' bg-[rgba(235,28,55,0.06)]';
    } else if (result.warnings.length > 0) {
      return ' bg-[rgba(230,161,0,0.08)]';
    } else {
      return ' bg-[rgba(31,154,58,0.08)]';
    }
  };

  return (
    <div className={`p-4 space-y-2 ${className}`}>
      <Label className="text-[12px] font-medium text-text-grey">
        {label} {required && <span className="text-hero-red">*</span>}
      </Label>

      <p className="text-[12px] text-heritage-grey">
        {description}
      </p>

      {/* NDT Technique Selector */}
      {/* {enableNDTValidation && (
        <div className="mb-4 p-3 bg-blue-50 border rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Info className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">NDT Validation Active</span>
          </div>
          <div className="text-xs text-blue-700">
            Selected Technique: <Badge variant="outline" className="ml-1">{ndtTechnique}</Badge>
          </div>
        </div>
      )} */}
      
      <FileUpload
        onFileSelect={handleFileSelect}
        accept={acceptedTypes}
        multiple={multiple}
        maxFiles={ maxFiles}
        buttonText={buttonText}
        variant={variant}
        size={size}
        onRemoveFile={handleRemoveFile}
        selectedFiles={files}
        showFileList={files.length > 0}
        className="w-full"
        maxHeight="400px"
        showTags={enableTagging}
        optionsTags={tagOptions}
        selectedTags={selectedTags}
        showAddButton={enableTagging}
        addButtonText="Add New Tag"
        onAddButtonClick={() => setIsAddingTag(true)}
        onTagsChange={handleTagsChange}
      />

    
      {/* Debug: Show current files count */}
      <div className="text-[11px] text-heritage-grey">
        Current files: {files.length} file(s)
      </div>

      {/* Add New Tag Dialog */}
      {enableTagging && (
        <AddNewItemDialog
          open={isAddingTag}
          onOpenChange={setIsAddingTag}
          title="Add New Tag"
          label="Tag Name"
          placeholder="Enter tag name"
          onAdd={handleAddTag}
          buttonText="Add Tag"
          cancelText="Cancel"
        />
      )}
    </div>
  );
}; 